import type { Bill } from '../services/bills-service';

/**
 * How far ahead an Upcoming bill starts needing attention.
 *
 * The server only calls a bill Due ON its due date (domain.Bill.EffectiveStatus)
 * and Overdue after it. That left a card due tomorrow sitting under "Upcoming",
 * so the "Needs action" tab read (0) the day before a payment was due -- the
 * one day it most needed to say something. A week covers a payment that takes
 * a few business days to post.
 */
export const ACTION_WINDOW_DAYS = 7;

/** YYYY-MM-DD for a local date. */
const ymd = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

/**
 * Whether a bill belongs on the "Needs action" tab.
 *
 * @param b The bill.
 * @param today Reference date (injectable for tests).
 * @returns True for Due and Overdue, for Upcoming bills due within
 *   ACTION_WINDOW_DAYS (unless a schedule is paying them), and for scheduled
 *   bills that need review or whose payment was not seen.
 */
export function needsAction(
  b: Pick<Bill, 'effective_status' | 'due_date'> & Partial<Pick<Bill, 'schedule_state'>>,
  today: Date = new Date(),
): boolean {
  // The scheduled-payment verifier gave up on these: the bill changed after it
  // was scheduled, or the payment never showed. Only a person can resolve
  // either, whatever the bill's own status says.
  if (b.schedule_state === 'needs_review' || b.schedule_state === 'not_seen') return true;
  if (b.effective_status === 'Due' || b.effective_status === 'Overdue') return true;
  if (b.effective_status !== 'Upcoming' || !b.due_date) return false;
  // A bill on a live schedule is being paid without anyone: "due within a
  // week" is not a reminder it needs. If it is not seen, the case above
  // brings it back.
  if (b.schedule_state === 'scheduled') return false;
  const horizon = new Date(today);
  horizon.setDate(horizon.getDate() + ACTION_WINDOW_DAYS);
  // String compare on YYYY-MM-DD: due dates are calendar days, and parsing
  // them as instants would shift them across midnight in western time zones.
  return b.due_date.slice(0, 10) <= ymd(horizon);
}
